import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Box, Typography, Grid, Chip, Divider,
    Table, TableHead, TableRow, TableCell, TableBody,
    TableContainer, Paper, Tabs, Tab, CircularProgress
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { orderListAPI } from '../../services/api';

const STATUS_COLOR = {
    pending: 'warning',
    approved: 'success',
    declined: 'error',
};

const PAYMENT_STATUS_COLOR = {
    unpaid: 'error',
    partially_paid: 'warning',
    fully_paid: 'success',
};

const PAYMENT_METHOD_COLOR = {
    cash: 'success',
    bank_transfer: 'info',
    cheque: 'warning',
    other: 'default',
};

const Row = ({ label, value, valueColor }) => (
    <Grid container spacing={1} mb={0.5}>
        <Grid item xs={5}>
            <Typography variant="body2" color="text.secondary">{label}</Typography>
        </Grid>
        <Grid item xs={7}>
            <Typography variant="body2" fontWeight={500} color={valueColor}>
                {value ?? '—'}
            </Typography>
        </Grid>
    </Grid>
);

const TabPanel = ({ children, value, index }) => (
    <div role="tabpanel" hidden={value !== index}>
        {value === index && <Box pt={2}>{children}</Box>}
    </div>
);

const OrderDetailDialog = ({ open, onClose, order }) => {
    const { t } = useTranslation();
    const [tabIndex, setTabIndex] = useState(0);
    const [payments, setPayments] = useState([]);
    const [paymentsLoading, setPaymentsLoading] = useState(false);

    const fetchPayments = useCallback(async () => {
        if (!order?._id) return;
        setPaymentsLoading(true);
        try {
            const res = await orderListAPI.getOrderPayments(order._id, {
                limit: 50,
                sortBy: 'paymentDate',
                sortOrder: 'desc'
            });
            setPayments(res.data?.data || []);
        } catch {
            setPayments([]);
        } finally {
            setPaymentsLoading(false);
        }
    }, [order?._id]);

    useEffect(() => {
        if (open && order) {
            setTabIndex(0);
            fetchPayments();
        }
    }, [open, order, fetchPayments]);

    if (!order) return null;

    const statusLabel =
        t(`orderList.status${order.status.charAt(0).toUpperCase() + order.status.slice(1)}`);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">
                        {t('orderList.orderSummary')} — {order.orderNumber}
                    </Typography>
                    <Box display="flex" gap={1}>
                        <Chip
                            label={statusLabel}
                            color={STATUS_COLOR[order.status] || 'default'}
                            size="small"
                        />
                        {order.paymentStatus && (
                            <Chip
                                label={t(`orderList.paymentStatus.${order.paymentStatus}`,
                                    order.paymentStatus.replace('_', ' '))}
                                color={PAYMENT_STATUS_COLOR[order.paymentStatus] || 'default'}
                                size="small"
                                variant="outlined"
                            />
                        )}
                    </Box>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                <Tabs
                    value={tabIndex}
                    onChange={(_, v) => setTabIndex(v)}
                    sx={{ mb: 1, borderBottom: 1, borderColor: 'divider' }}
                >
                    <Tab label={t('orderList.tabs.details', 'Order Details')} />
                    <Tab
                        label={
                            <Box display="flex" alignItems="center" gap={1}>
                                {t('orderList.tabs.payments', 'Payments')}
                                {payments.length > 0 && (
                                    <Chip label={payments.length} size="small" color="primary" />
                                )}
                            </Box>
                        }
                    />
                </Tabs>

                {/* ── Tab 0: Order Details ───────────────────────────── */}
                <TabPanel value={tabIndex} index={0}>
                    <Grid container spacing={3}>
                        {/* Left: order info */}
                        <Grid item xs={12} md={5}>
                            <Typography variant="subtitle2" fontWeight={600} mb={1}>
                                {t('orderList.orderSummary')}
                            </Typography>

                            <Row label={t('orderList.supplier')}
                                value={order.supplier?.name} />
                            <Row label={t('common.createdAt')}
                                value={order.createdAt
                                    ? new Date(order.createdAt).toLocaleString()
                                    : '—'} />
                            <Row label={t('orderList.createdBy')}
                                value={order.createdBy?.username} />

                            {order.status === 'approved' && (
                                <>
                                    <Row label={t('orderList.approvedBy')}
                                        value={order.approvedBy?.username} />
                                    <Row label={t('orderList.approvedAt')}
                                        value={order.approvedAt
                                            ? new Date(order.approvedAt).toLocaleString()
                                            : '—'} />
                                </>
                            )}

                            {order.status === 'declined' && (
                                <>
                                    <Row label={t('orderList.declinedBy')}
                                        value={order.declinedBy?.username} />
                                    <Row label={t('orderList.declinedAt')}
                                        value={order.declinedAt
                                            ? new Date(order.declinedAt).toLocaleString()
                                            : '—'} />
                                    <Row label={t('orderList.declineReason')}
                                        value={order.declineReason} />
                                </>
                            )}

                            {order.notes && (
                                <Row label={t('orderList.notes')} value={order.notes} />
                            )}

                            <Divider sx={{ my: 1.5 }} />

                            {/* Financial summary */}
                            <Typography variant="subtitle2" fontWeight={600} mb={1}>
                                {t('orderList.financialSummary')}
                            </Typography>

                            <Row label={t('orderList.totalPrice')}
                                value={`${order.totalPrice?.toLocaleString()} ${t('common.currency')}`} />
                            <Row label={t('orderList.paidPrice')}
                                value={`${order.paidPrice?.toLocaleString()} ${t('common.currency')}`} />
                            <Row
                                label={t('orderList.remainingBalance')}
                                value={`${order.remainingBalance?.toLocaleString()} ${t('common.currency')}`}
                                valueColor={order.remainingBalance > 0 ? 'error.main' : 'success.main'}
                            />
                        </Grid>

                        {/* Right: items table */}
                        <Grid item xs={12} md={7}>
                            <Typography variant="subtitle2" fontWeight={600} mb={1}>
                                {t('orderList.items')}
                            </Typography>

                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell><strong>{t('orderList.itemName')}</strong></TableCell>
                                            <TableCell align="right"><strong>{t('orderList.quantity')}</strong></TableCell>
                                            <TableCell align="right"><strong>{t('orderList.unitPrice')}</strong></TableCell>
                                            <TableCell align="right"><strong>{t('orderList.totalPrice')}</strong></TableCell>
                                            <TableCell align="right"><strong>{t('orderList.previousAvgCost')}</strong></TableCell>
                                            <TableCell align="right"><strong>{t('orderList.newAvgCost')}</strong></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {(order.items || []).map((item, i) => (
                                            <TableRow key={i}>
                                                <TableCell>
                                                    {item.itemName || item.centralStockItem?.name || '—'}
                                                </TableCell>
                                                <TableCell align="right">{item.quantity}</TableCell>
                                                <TableCell align="right">{item.unitPrice?.toFixed(2)}</TableCell>
                                                <TableCell align="right">{item.totalPrice?.toFixed(2)}</TableCell>
                                                <TableCell align="right">{item.previousAvgCost?.toFixed(2) ?? '—'}</TableCell>
                                                <TableCell align="right">
                                                    {order.status === 'approved'
                                                        ? item.newAvgCost?.toFixed(2) ?? '—'
                                                        : '—'}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Grid>
                    </Grid>
                </TabPanel>

                {/* ── Tab 1: Payment History ─────────────────────────── */}
                <TabPanel value={tabIndex} index={1}>
                    {paymentsLoading ? (
                        <Box display="flex" justifyContent="center" py={4}>
                            <CircularProgress size={32} />
                        </Box>
                    ) : payments.length === 0 ? (
                        <Box textAlign="center" py={4}>
                            <Typography color="text.secondary">
                                {t('orderList.paymentHistory.noPayments', 'No payments recorded yet')}
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            {/* Summary bar */}
                            <Box
                                sx={{
                                    p: 2, mb: 2, borderRadius: 1,
                                    bgcolor: 'grey.50',
                                    border: '1px solid',
                                    borderColor: 'grey.200'
                                }}
                            >
                                <Grid container spacing={2}>
                                    <Grid item xs={3}>
                                        <Typography variant="caption" color="text.secondary">
                                            {t('orderList.paymentHistory.totalPayments', 'Total Payments')}
                                        </Typography>
                                        <Typography variant="h6" fontWeight={600}>
                                            {payments.length}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Typography variant="caption" color="text.secondary">
                                            {t('orderList.totalPrice')}
                                        </Typography>
                                        <Typography variant="body1" fontWeight={500}>
                                            {order.totalPrice?.toLocaleString()} {t('common.currency')}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Typography variant="caption" color="text.secondary">
                                            {t('orderList.paidPrice')}
                                        </Typography>
                                        <Typography variant="body1" fontWeight={500} color="success.main">
                                            {order.paidPrice?.toLocaleString()} {t('common.currency')}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Typography variant="caption" color="text.secondary">
                                            {t('orderList.remainingBalance')}
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            fontWeight={600}
                                            color={order.remainingBalance > 0 ? 'error.main' : 'success.main'}
                                        >
                                            {order.remainingBalance?.toLocaleString()} {t('common.currency')}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Box>

                            {/* Payment records table */}
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell><strong>#</strong></TableCell>
                                            <TableCell>
                                                <strong>{t('orderList.paymentHistory.date', 'Date')}</strong>
                                            </TableCell>
                                            <TableCell align="right">
                                                <strong>{t('orderList.paymentHistory.amount', 'Amount')}</strong>
                                            </TableCell>
                                            <TableCell>
                                                <strong>{t('orderList.paymentHistory.branch', 'Branch')}</strong>
                                            </TableCell>
                                            <TableCell>
                                                <strong>{t('orderList.paymentHistory.method', 'Method')}</strong>
                                            </TableCell>
                                            <TableCell align="right">
                                                <strong>{t('orderList.paymentHistory.balanceAfter', 'Balance After')}</strong>
                                            </TableCell>
                                            <TableCell>
                                                <strong>{t('orderList.paymentHistory.recordedBy', 'Recorded By')}</strong>
                                            </TableCell>
                                            <TableCell>
                                                <strong>{t('orderList.paymentHistory.notes', 'Notes')}</strong>
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {payments.map((payment, index) => (
                                            <TableRow key={payment._id} hover>
                                                <TableCell>{index + 1}</TableCell>
                                                <TableCell>
                                                    {payment.paymentDate
                                                        ? new Date(payment.paymentDate).toLocaleDateString()
                                                        : '—'}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Typography fontWeight={600} color="primary.main">
                                                        {payment.amount?.toLocaleString()} {t('common.currency')}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    {payment.branch?.name || '—'}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={t(
                                                            `orderList.payment.methods.${payment.paymentMethod}`,
                                                            payment.paymentMethod?.replace('_', ' ') || 'cash'
                                                        )}
                                                        color={PAYMENT_METHOD_COLOR[payment.paymentMethod] || 'default'}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight={500}
                                                        color={payment.balanceAfter > 0 ? 'error.main' : 'success.main'}
                                                    >
                                                        {payment.balanceAfter?.toLocaleString()} {t('common.currency')}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    {payment.createdBy?.username || '—'}
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{
                                                            maxWidth: 120,
                                                            display: 'block',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap'
                                                        }}
                                                        title={payment.notes || ''}
                                                    >
                                                        {payment.notes || '—'}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    )}
                </TabPanel>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>{t('common.close')}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default OrderDetailDialog;